import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeSearchQuery } from '../utils/textSanitize';
import { consultarCNPJ, validarCNPJ } from '../services/cnpjService';
import { consultarCEP, validarCEP } from '../services/cepService';
import { 
  Building, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  MapPin,
  Phone,
  Mail,
  FileText,
  Calendar,
  AlertCircle,
  CheckCircle,
  X,
  User,
  Power,
  XCircle
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase'; // ✅ CORREÇÃO: Caminho correto
import NovaEmpresaModal from './NovaEmpresaModal'; // ✅ NOVO: Usar componente modal
import { MENU_MODULOS, modulosIniciais } from '../config/menuModulos';

const CompaniesPage = () => {
  const { user, userData, isRole } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCompanies, setTotalCompanies] = useState(0);

  // Estados para consulta de CNPJ
  const [consultandoCNPJ, setConsultandoCNPJ] = useState(false);
  const [erroCNPJ, setErroCNPJ] = useState('');

  // Estados para consulta de CEP
  const [consultandoCEP, setConsultandoCEP] = useState(false);
  const [erroCEP, setErroCEP] = useState('');

  // Estados do formulário
  const [formData, setFormData] = useState({
    nome_empresa: '',
    nome_fantasia: '',
    cnpj: '',
    email: '',
    email_financeiro: '',
    telefone: '',
    endereco: {
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: ''
    },
    responsavel_legal: '',
    inscricao_estadual: '',
    inscricao_municipal: '',
    // ✅ ADICIONADO: Campos do administrador
    admin_nome: '',
    admin_email: '',
    modulo_armazenamento: false,
    modulos: modulosIniciais(null)
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Funções de formatação
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return numbers.replace(/(\d{2})(\d{0,3})/, '$1.$2');
    if (numbers.length <= 8) return numbers.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
    if (numbers.length <= 12) return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return numbers.replace(/(\d{5})(\d{0,3})/, '$1-$2');
  };

  const formatTelefone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  // Funções do Firebase
  const listCompanies = httpsCallable(functions, 'listCompanies');
  const createCompany = httpsCallable(functions, 'createCompany');
  const updateCompany = httpsCallable(functions, 'updateCompany');
  const getCompany = httpsCallable(functions, 'getCompany');

  // Carregar empresas
  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔍 CompaniesPage: Carregando empresas...');

      const result = await listCompanies({
        page: currentPage,
        limit: 20,
        ativo: statusFilter === 'all' ? undefined : statusFilter === 'active',
        search: searchTerm?.trim() ? sanitizeSearchQuery(searchTerm) : undefined
      });

      console.log('🔍 CompaniesPage: Resultado da listCompanies:', result);

      if (result.data?.success) {
        const companiesData = result.data.data;
        setCompanies(companiesData.items || []);
        setTotalPages(companiesData.pagination?.totalPages || 1);
        setTotalCompanies(companiesData.pagination?.total || 0);
        
        console.log('✅ CompaniesPage: Empresas carregadas:', companiesData.items?.length || 0);
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar empresas');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar empresas:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Função para tratar erros
  const getErrorMessage = (error) => {
    console.log('🔍 Analisando erro:', error);
    
    if (error?.code) {
      switch (error.code) {
        case 'permission-denied':
          return 'Você não tem permissão para acessar as empresas.';
        case 'unauthenticated':
          return 'Sessão expirada. Faça login novamente.';
        case 'not-found':
          return 'Empresas não encontradas.';
        case 'failed-precondition':
          return 'Erro de configuração. Contate o administrador.';
        default:
          return error.message || 'Erro desconhecido ao carregar empresas.';
      }
    }
    
    return error?.message || 'Erro ao carregar empresas. Tente novamente.';
  };

  // Carregar empresas quando a página carrega ou filtros mudam
  useEffect(() => {
    loadCompanies();
  }, [currentPage, statusFilter]);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadCompanies();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Abrir modal para criar empresa
  const handleCreate = () => {
    setEditingCompany(null);
    setFormData({
      nome_empresa: '',
      nome_fantasia: '',
      cnpj: '',
      email: '',
      email_financeiro: '',
      telefone: '',
      endereco: {
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: ''
      },
      responsavel_legal: '',
      inscricao_estadual: '',
      inscricao_municipal: '',
      // ✅ ADICIONADO: Campos do administrador
      admin_nome: '',
      admin_email: '',
      modulo_armazenamento: false,
      modulos: modulosIniciais(null)
    });
    setFormErrors({});
    setErroCNPJ(''); // Limpa erro de CNPJ
    setErroCEP(''); // Limpa erro de CEP
    setShowModal(true);
  };

  // Abrir modal para editar empresa
  const handleEdit = async (company) => {
    try {
      setEditingCompany(company);
      setFormData({
        nome_empresa: company.nome_empresa || '',
        nome_fantasia: company.nome_fantasia || '',
        cnpj: company.cnpj || '',
        email: company.email || '',
        telefone: company.telefone || '',
        endereco: {
          logradouro: company.endereco?.rua || '', // ✅ CORREÇÃO: rua em vez de logradouro
          numero: company.endereco?.numero || '',
          complemento: company.endereco?.complemento || '',
          bairro: company.endereco?.bairro || '',
          cidade: company.endereco?.cidade || '',
          estado: company.endereco?.estado || '',
          cep: company.endereco?.cep || ''
        },
        responsavel_legal: company.responsavel_legal || '',
        inscricao_estadual: company.inscricao_estadual || '',
        inscricao_municipal: company.inscricao_municipal || '',
        // ✅ ADICIONADO: Campos do administrador
        admin_nome: company.admin_nome || '',
        admin_email: company.admin_email || '',
        email_financeiro: company.email_financeiro || '',
        modulo_armazenamento: company.modulo_armazenamento === true,
        modulos: modulosIniciais(company)
      });
      setFormErrors({});
      setErroCNPJ(''); // Limpa erro de CNPJ
      setErroCEP(''); // Limpa erro de CEP
      setShowModal(true);
    } catch (err) {
      console.error('Erro ao carregar dados da empresa:', err);
      setError('Erro ao carregar dados da empresa');
    }
  };

  // Abrir modal para visualizar empresa
  const handleView = (company) => {
    setViewingCompany(company);
    setShowViewModal(true);
  };

  // ✅ NOVA FUNÇÃO: Alternar status ativo/inativo da empresa
  const handleToggleStatus = async (company) => {
    const novoStatus = !company.ativo;
    const acao = novoStatus ? 'ativar' : 'inativar';
    
    if (!window.confirm(`Deseja realmente ${acao} a empresa "${company.nome_empresa}"?`)) {
      return;
    }

    try {
      setLoading(true);
      const updateCompany = httpsCallable(functions, 'updateCompany');
      
      const result = await updateCompany({
        companyId: company.id,
        ativo: novoStatus
      });

      if (result.data.success) {
        await loadCompanies();
        alert(`Empresa ${novoStatus ? 'ativada' : 'inativada'} com sucesso!`);
      } else {
        throw new Error(result.data.message || 'Erro ao atualizar status');
      }
    } catch (err) {
      console.error('Erro ao atualizar status da empresa:', err);
      setError(`Erro ao ${acao} empresa: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Função para consultar CEP na API ViaCEP
  const handleConsultarCEP = async () => {
    const cepLimpo = formData.endereco.cep.replace(/\D/g, '');
    
    // Valida se o CEP tem 8 dígitos
    if (cepLimpo.length !== 8) {
      setErroCEP('CEP deve ter 8 dígitos');
      return;
    }

    // Valida se o CEP é válido
    if (!validarCEP(formData.endereco.cep)) {
      setErroCEP('CEP inválido');
      return;
    }

    setConsultandoCEP(true);
    setErroCEP('');

    try {
      const resultado = await consultarCEP(formData.endereco.cep);
      
      if (resultado.success) {
        // Preenche os campos com os dados da API
        setFormData(prev => ({
          ...prev,
          endereco: {
            ...prev.endereco,
            logradouro: resultado.data.logradouro,
            bairro: resultado.data.bairro,
            cidade: resultado.data.cidade,
            estado: resultado.data.estado,
            complemento: resultado.data.complemento || prev.endereco.complemento
          }
        }));

        // Limpa erros de validação dos campos preenchidos
        setFormErrors(prev => ({
          ...prev,
          logradouro: '',
          bairro: '',
          cidade: '',
          estado: ''
        }));

        console.log('✅ Dados do CEP preenchidos automaticamente');
      } else {
        setErroCEP(resultado.error || 'Erro ao consultar CEP');
      }
    } catch (error) {
      console.error('❌ Erro ao consultar CEP:', error);
      setErroCEP('Erro ao consultar CEP. Tente novamente.');
    } finally {
      setConsultandoCEP(false);
    }
  };

  // Validar formulário
  const validateForm = () => {
    const errors = {};

    if (!formData.nome_empresa.trim()) {
      errors.nome_empresa = 'Nome da empresa é obrigatório';
    }

    if (!formData.cnpj.trim()) {
      errors.cnpj = 'CNPJ é obrigatório';
    } else if (!/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(formData.cnpj)) {
      errors.cnpj = 'CNPJ deve estar no formato XX.XXX.XXX/XXXX-XX';
    }


    if (!formData.telefone.trim()) {
      errors.telefone = 'Telefone é obrigatório';
    }

    if (!formData.endereco.logradouro.trim()) {
      errors.logradouro = 'Logradouro é obrigatório';
    }

    if (!formData.endereco.numero.trim()) {
      errors.numero = 'Número é obrigatório';
    }

    if (!formData.endereco.bairro.trim()) {
      errors.bairro = 'Bairro é obrigatório';
    }

    if (!formData.endereco.cidade.trim()) {
      errors.cidade = 'Cidade é obrigatória';
    }

    if (!formData.endereco.estado.trim()) {
      errors.estado = 'Estado é obrigatório';
    }

    if (!formData.endereco.cep.trim()) {
      errors.cep = 'CEP é obrigatório';
    } else if (!/^\d{5}-\d{3}$/.test(formData.endereco.cep)) {
      errors.cep = 'CEP deve estar no formato 00000-000';
    }

    // ✅ ADICIONADO: Validação dos campos do administrador
    if (!formData.admin_nome.trim()) {
      errors.admin_nome = 'Nome do administrador é obrigatório';
    }

    if (!formData.admin_email.trim()) {
      errors.admin_email = 'Email do administrador é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
      errors.admin_email = 'Email do administrador inválido';
    }


    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Função para consultar CNPJ na Receita Federal
  const handleConsultarCNPJ = async () => {
    const cnpjLimpo = formData.cnpj.replace(/\D/g, '');
    
    // Valida se o CNPJ tem 14 dígitos
    if (cnpjLimpo.length !== 14) {
      setErroCNPJ('CNPJ deve ter 14 dígitos');
      return;
    }

    // Valida se o CNPJ é válido
    if (!validarCNPJ(formData.cnpj)) {
      setErroCNPJ('CNPJ inválido');
      return;
    }

    setConsultandoCNPJ(true);
    setErroCNPJ('');

    try {
      const resultado = await consultarCNPJ(formData.cnpj);
      
      if (resultado.success) {
        // Preenche os campos com os dados da API
        setFormData(prev => ({
          ...prev,
          nome_empresa: resultado.data.nome_empresa,
          nome_fantasia: resultado.data.nome_fantasia,
          telefone: resultado.data.telefone,
          endereco: {
            ...prev.endereco,
            logradouro: resultado.data.endereco.logradouro,
            numero: resultado.data.endereco.numero,
            complemento: resultado.data.endereco.complemento,
            bairro: resultado.data.endereco.bairro,
            cidade: resultado.data.endereco.cidade,
            estado: resultado.data.endereco.estado,
            // ✅ CORREÇÃO: Formatar CEP removendo ponto e aplicando formato correto
            cep: resultado.data.endereco.cep ? 
              resultado.data.endereco.cep.replace(/[^\d]/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2') : 
              ''
          },
          inscricao_estadual: resultado.data.inscricao_estadual
        }));

        // Limpa erros de validação dos campos preenchidos
        setFormErrors(prev => ({
          ...prev,
          nome_empresa: '',
          telefone: '',
          endereco: {
            ...prev.endereco,
            logradouro: '',
            numero: '',
            bairro: '',
            cidade: '',
            estado: '',
            cep: ''
          }
        }));

        console.log('✅ Dados do CNPJ preenchidos automaticamente');
      } else {
        setErroCNPJ(resultado.error || 'Erro ao consultar CNPJ');
      }
    } catch (error) {
      console.error('❌ Erro ao consultar CNPJ:', error);
      setErroCNPJ('Erro ao consultar CNPJ. Tente novamente.');
    } finally {
      setConsultandoCNPJ(false);
    }
  };

// ✅ SUBSTITUIR APENAS A FUNÇÃO handleSave (linha ~240-290)
// Localizar esta função no seu arquivo e substituir por esta versão corrigida:

const handleSave = async () => {
  if (!validateForm()) {
    return;
  }

  try {
    setSubmitting(true);
    setError('');

    if (editingCompany) {
      // ✅ ATUALIZAR EMPRESA EXISTENTE
      const updateData = {
        companyId: editingCompany.id,  // ✅ CORREÇÃO: company_id -> companyId
        nome_empresa: formData.nome_empresa.trim(),
        nome_fantasia: formData.nome_fantasia.trim(),
        cnpj: formData.cnpj.trim(),
        email_empresa: formData.admin_email.trim(),  // ✅ USAR EMAIL DO ADMIN
        email_financeiro: formData.email_financeiro.trim(),
        telefone: formData.telefone.trim(),
        endereco: {
          rua: formData.endereco.logradouro.trim(),  // ✅ CORREÇÃO: logradouro -> rua
          numero: formData.endereco.numero.trim(),
          complemento: formData.endereco.complemento.trim(),
          bairro: formData.endereco.bairro.trim(),
          cidade: formData.endereco.cidade.trim(),
          estado: formData.endereco.estado.trim(),
          cep: formData.endereco.cep.trim()
        },
        responsavel_legal: formData.responsavel_legal.trim(),
        inscricao_estadual: formData.inscricao_estadual.trim(),
        inscricao_municipal: formData.inscricao_municipal.trim(),
        // ✅ ADICIONADO: Campos do administrador
        admin_nome: formData.admin_nome.trim(),
        admin_email: formData.admin_email.trim(),
        modulo_armazenamento: formData.modulos?.armazenamento === true,
        modulos: formData.modulos
      };

      const result = await updateCompany(updateData);

      if (result.data?.success) {
        setShowModal(false);
        loadCompanies();
      } else {
        throw new Error(result.data?.message || 'Erro ao atualizar empresa');
      }
    } else {
      // ✅ CRIAR NOVA EMPRESA COM SISTEMA DE CONVITES
      const createData = {
        nome_empresa: formData.nome_empresa.trim(),
        nome_fantasia: formData.nome_fantasia.trim(),
        cnpj: formData.cnpj.trim(),
        email_empresa: formData.admin_email.trim(),  // ✅ USAR EMAIL DO ADMIN COMO EMAIL DA EMPRESA
        email_financeiro: formData.email_financeiro.trim(),
        telefone: formData.telefone.trim(),
        endereco: {
          rua: formData.endereco.logradouro.trim(),  // ✅ CORREÇÃO: logradouro -> rua
          numero: formData.endereco.numero.trim(),
          complemento: formData.endereco.complemento.trim(),
          bairro: formData.endereco.bairro.trim(),
          cidade: formData.endereco.cidade.trim(),
          estado: formData.endereco.estado.trim(),
          cep: formData.endereco.cep.trim()
        },
        responsavel_legal: formData.responsavel_legal.trim(),
        inscricao_estadual: formData.inscricao_estadual.trim(),
        inscricao_municipal: formData.inscricao_municipal.trim(),
        // ✅ CAMPOS PARA SISTEMA DE CONVITES
        admin_nome: formData.admin_nome.trim(),  // ✅ ADICIONADO: Nome do admin
        admin_email: formData.admin_email.trim()  // ✅ ADICIONADO: Email do admin
      };

      console.log('🔍 Enviando dados para createCompany:', createData);

      const result = await createCompany(createData);

      if (result.data?.success) {
        setShowModal(false);
        loadCompanies();
        
        // ✅ MOSTRAR MENSAGEM DE SUCESSO COM INFORMAÇÕES DO CONVITE
        if (result.data.data?.convite) {
          alert(`Empresa criada com sucesso!\n\nConvite enviado para: ${formData.email}\nO administrador receberá um email para ativar a conta.`);
        } else {
          alert('Empresa criada com sucesso!');
        }
      } else {
        throw new Error(result.data?.message || 'Erro ao criar empresa');
      }
    }
  } catch (err) {
    console.error('❌ Erro ao salvar empresa:', err);
    setError(getErrorMessage(err));
  } finally {
    setSubmitting(false);
  }
};



  // Formatação para exibição
  const formatPhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
  };

  // ✅ FUNÇÃO FORMATDATE CORRIGIDA PARA TRATAR TIMESTAMPS DO FIRESTORE
  const formatDate = (date) => {
    if (!date) return '';
    
    try {
      // ✅ TRATAR DIFERENTES TIPOS DE DATA DO FIRESTORE
      let dateObj;
      
      // Se é um timestamp do Firestore (objeto com seconds e nanoseconds)
      if (date && typeof date === 'object' && date.seconds) {
        dateObj = new Date(date.seconds * 1000);
      }
      // Se é um timestamp do Firestore (objeto com _seconds)
      else if (date && typeof date === 'object' && date._seconds) {
        dateObj = new Date(date._seconds * 1000);
      }
      // Se é uma string de data ISO
      else if (typeof date === 'string') {
        dateObj = new Date(date);
      }
      // Se é um número (timestamp em milliseconds)
      else if (typeof date === 'number') {
        dateObj = new Date(date);
      }
      // Se já é um objeto Date
      else if (date instanceof Date) {
        dateObj = date;
      }
      // Caso contrário, tentar converter diretamente
      else {
        dateObj = new Date(date);
      }
      
      // ✅ VERIFICAR SE A DATA É VÁLIDA
      if (isNaN(dateObj.getTime())) {
        console.warn('⚠️ Data inválida recebida:', date);
        return 'Data inválida';
      }
      
      // ✅ FORMATAR PARA PORTUGUÊS BRASILEIRO
      return dateObj.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
    } catch (error) {
      console.error('❌ Erro ao formatar data:', error, 'Data recebida:', date);
      return 'Erro na data';
    }
  };

  // Estados brasileiros
  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  // ✅ CORREÇÃO: Verificar se é superadmin usando isRole()
  if (!isRole('superadmin')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acesso negado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Apenas superadministradores podem acessar esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Empresas</h1>
          <p className="text-gray-600">Gerencie as empresas do sistema</p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Nova Empresa</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por nome, CNPJ ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todas</option>
              <option value="active">Ativas</option>
              <option value="inactive">Inativas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erro</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={loadCompanies}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de empresas */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : companies.length === 0 ? (
        <div className="text-center py-12">
          <Building className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma empresa encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando uma nova empresa.'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CNPJ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Localização
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criada em
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Building className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {company.nome_empresa}
                          </div>
                          {company.responsavel_legal && (
                            <div className="text-sm text-gray-500">
                              Resp.: {company.responsavel_legal}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCNPJ(company.cnpj)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Mail className="h-4 w-4 text-gray-400 mr-1" />
                        {company.email}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <Phone className="h-4 w-4 text-gray-400 mr-1" />
                        {formatPhone(company.telefone)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                        {company.endereco?.cidade}, {company.endereco?.estado}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        company.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {company.ativo ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Ativa
                          </>
                        ) : (
                          <>
                            <X className="h-3 w-3 mr-1" />
                            Inativa
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                        {formatDate(company.criado_em)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(company)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Editar empresa"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleView(company)}
                          className="text-gray-600 hover:text-gray-900 p-1 rounded"
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(company)}
                          className={`p-1 rounded ${
                            company.ativo 
                              ? 'text-red-600 hover:text-red-900' 
                              : 'text-green-600 hover:text-green-900'
                          }`}
                          title={company.ativo ? 'Inativar empresa' : 'Ativar empresa'}
                        >
                          {company.ativo ? (
                            <XCircle className="h-4 w-4" />
                          ) : (
                            <Power className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando{' '}
                    <span className="font-medium">{(currentPage - 1) * 20 + 1}</span>
                    {' '}até{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * 20, totalCompanies)}
                    </span>
                    {' '}de{' '}
                    <span className="font-medium">{totalCompanies}</span>
                    {' '}resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    
                    {/* Números das páginas */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próxima
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Criar Empresa - Usando componente reutilizável */}
      {showModal && !editingCompany && (
        <NovaEmpresaModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={(result) => {
            setShowModal(false);
            loadCompanies();
            alert(result.message || 'Empresa criada com sucesso!');
          }}
          initialData={formData}
        />
      )}

      {/* Modal de Editar Empresa - Formulário inline (TODO: refatorar) */}
      {showModal && editingCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Editar Empresa
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={submitting}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Campo CNPJ - Primeiro campo do formulário */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CNPJ *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) => {
                      const formatted = formatCNPJ(e.target.value);
                      if (formatted.replace(/\D/g, '').length <= 14) {
                        setFormData({ ...formData, cnpj: formatted });
                        setErroCNPJ(''); // Limpa erro ao digitar
                      }
                    }}
                    className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.cnpj ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="00.000.000/0000-00"
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={handleConsultarCNPJ}
                    disabled={consultandoCNPJ || submitting || formData.cnpj.replace(/\D/g, '').length !== 14}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {consultandoCNPJ ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Consultando...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4" />
                        Consultar
                      </>
                    )}
                  </button>
                </div>
                {formErrors.cnpj && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.cnpj}</p>
                )}
                {erroCNPJ && (
                  <p className="text-red-500 text-xs mt-1">{erroCNPJ}</p>
                )}
              </div>

              {/* Informações básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Empresa *
                  </label>
                  <input
                    type="text"
                    value={formData.nome_empresa}
                    onChange={(e) => setFormData({ ...formData, nome_empresa: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.nome_empresa ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Empresa ABC Ltda"
                    disabled={submitting}
                  />
                  {formErrors.nome_empresa && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.nome_empresa}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Fantasia
                  </label>
                  <input
                    type="text"
                    value={formData.nome_fantasia}
                    onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.nome_fantasia ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: ABC"
                    disabled={submitting}
                  />
                  {formErrors.nome_fantasia && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.nome_fantasia}</p>
                  )}
                </div>



                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone *
                  </label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => {
                      const formatted = formatTelefone(e.target.value);
                      if (formatted.replace(/\D/g, '').length <= 11) {
                        setFormData({ ...formData, telefone: formatted });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.telefone ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="(11) 99999-9999"
                    disabled={submitting}
                  />
                  {formErrors.telefone && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.telefone}</p>
                  )}
                </div>
              </div>

              {/* Endereço */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  Endereço
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logradouro *
                    </label>
                    <input
                      type="text"
                      value={formData.endereco.logradouro}
                      onChange={(e) => setFormData({
                        ...formData,
                        endereco: { ...formData.endereco, logradouro: e.target.value }
                      })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.logradouro ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Rua, Avenida, etc."
                      disabled={submitting}
                    />
                    {formErrors.logradouro && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.logradouro}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número *
                    </label>
                    <input
                      type="text"
                      value={formData.endereco.numero}
                      onChange={(e) => setFormData({
                        ...formData,
                        endereco: { ...formData.endereco, numero: e.target.value }
                      })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.numero ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="123"
                      disabled={submitting}
                    />
                    {formErrors.numero && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.numero}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={formData.endereco.complemento}
                      onChange={(e) => setFormData({
                        ...formData,
                        endereco: { ...formData.endereco, complemento: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Sala, Andar, etc."
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bairro *
                    </label>
                    <input
                      type="text"
                      value={formData.endereco.bairro}
                      onChange={(e) => setFormData({
                        ...formData,
                        endereco: { ...formData.endereco, bairro: e.target.value }
                      })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.bairro ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Centro"
                      disabled={submitting}
                    />
                    {formErrors.bairro && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.bairro}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cidade *
                    </label>
                    <input
                      type="text"
                      value={formData.endereco.cidade}
                      onChange={(e) => setFormData({
                        ...formData,
                        endereco: { ...formData.endereco, cidade: e.target.value }
                      })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.cidade ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="São Paulo"
                      disabled={submitting}
                    />
                    {formErrors.cidade && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.cidade}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado *
                    </label>
                    <select
                      value={formData.endereco.estado}
                      onChange={(e) => setFormData({
                        ...formData,
                        endereco: { ...formData.endereco, estado: e.target.value }
                      })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.estado ? 'border-red-300' : 'border-gray-300'
                      }`}
                      disabled={submitting}
                    >
                      <option value="">Selecione</option>
                      {estados.map(estado => (
                        <option key={estado} value={estado}>{estado}</option>
                      ))}
                    </select>
                    {formErrors.estado && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.estado}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CEP *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.endereco.cep}
                        onChange={(e) => {
                          const formatted = formatCEP(e.target.value);
                          if (formatted.replace(/\D/g, '').length <= 8) {
                            setFormData({
                              ...formData,
                              endereco: { ...formData.endereco, cep: formatted }
                            });
                            setErroCEP(''); // Limpa erro ao digitar
                          }
                        }}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formErrors.cep ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="00000-000"
                        disabled={submitting}
                      />
                      <button
                        type="button"
                        onClick={handleConsultarCEP}
                        disabled={consultandoCEP || submitting || formData.endereco.cep.replace(/\D/g, '').length !== 8}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {consultandoCEP ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Consultando...
                          </>
                        ) : (
                          <>
                            <Search className="h-4 w-4" />
                            CEP
                          </>
                        )}
                      </button>
                    </div>
                    {formErrors.cep && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.cep}</p>
                    )}
                    {erroCEP && (
                      <p className="text-red-500 text-xs mt-1">{erroCEP}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Informações adicionais */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Informações Adicionais
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Responsável Legal
                    </label>
                    <input
                      type="text"
                      value={formData.responsavel_legal}
                      onChange={(e) => setFormData({ ...formData, responsavel_legal: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nome do responsável"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Inscrição Estadual
                    </label>
                    <input
                      type="text"
                      value={formData.inscricao_estadual}
                      onChange={(e) => setFormData({ ...formData, inscricao_estadual: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="000.000.000.000"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Inscrição Municipal
                    </label>
                    <input
                      type="text"
                      value={formData.inscricao_municipal}
                      onChange={(e) => setFormData({ ...formData, inscricao_municipal: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="000000000"
                      disabled={submitting}
                    />
                  </div>
                </div>
              </div>

              {/* ✅ ADICIONADO: Informações do Administrador */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Administrador da Empresa
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Administrador *
                    </label>
                    <input
                      type="text"
                      value={formData.admin_nome}
                      onChange={(e) => setFormData({ ...formData, admin_nome: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.admin_nome ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Nome completo do administrador"
                      disabled={submitting}
                    />
                    {formErrors.admin_nome && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.admin_nome}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email do Administrador *
                    </label>
                    <input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.admin_email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="admin@empresa.com"
                      disabled={submitting}
                    />
                    {formErrors.admin_email && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.admin_email}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* ✅ ADICIONADO: E-mail Financeiro */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Mail className="h-5 w-5 mr-2" />
                  E-mail Financeiro
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail Financeiro - Contas a Pagar
                  </label>
                  <input
                    type="email"
                    value={formData.email_financeiro}
                    onChange={(e) => setFormData({ ...formData, email_financeiro: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.email_financeiro ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="financeiro@empresa.com"
                    disabled={submitting}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    E-mail que receberá as faturas do QualiManager
                  </p>
                  {formErrors.email_financeiro && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.email_financeiro}</p>
                  )}
                </div>
              </div>

              {editingCompany && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Acesso ao menu</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Liga o que esta empresa pode ver. O papel de cada usuário continua limitando o acesso dentro do que estiver ligado.
                  </p>
                  {['Operação', 'Cadastros', 'Produção', 'BPF', 'Gestão'].map((grupo) => (
                    <div key={grupo} className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">{grupo}</h4>
                      <div className="space-y-2">
                        {MENU_MODULOS.filter((item) => item.grupo === grupo).map((item) => (
                          <label key={item.id} className="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-3">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={formData.modulos?.[item.id] === true}
                              onChange={(e) => {
                                const ligado = e.target.checked;
                                const modulos = { ...(formData.modulos || {}), [item.id]: ligado };
                                if (!ligado) {
                                  MENU_MODULOS.filter((filho) => filho.requer === item.id).forEach((filho) => {
                                    modulos[filho.id] = false;
                                  });
                                }
                                if (ligado && item.requer) modulos[item.requer] = true;
                                setFormData({
                                  ...formData,
                                  modulos,
                                  modulo_armazenamento: modulos.armazenamento === true
                                });
                              }}
                              disabled={submitting}
                            />
                            <span>
                              <span className="block text-sm font-medium text-gray-900">{item.label}</span>
                              <span className="block text-xs text-gray-500">{item.descricao}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Erro do formulário */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Erro</h3>
                      <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer do modal */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </>
                ) : (
                  editingCompany ? 'Atualizar' : 'Criar Empresa'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualização */}
      {showViewModal && viewingCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Detalhes da Empresa
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informações básicas */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Building className="h-5 w-5 mr-2" />
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Empresa
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.nome_empresa || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Fantasia
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.nome_fantasia || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNPJ
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {formatCNPJ(viewingCompany.cnpj) || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {formatPhone(viewingCompany.telefone) || 'Não informado'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  Endereço
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logradouro
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.endereco?.rua || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.endereco?.numero || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Complemento
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.endereco?.complemento || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bairro
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.endereco?.bairro || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cidade
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.endereco?.cidade || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.endereco?.estado || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CEP
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.endereco?.cep || 'Não informado'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Informações adicionais */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Informações Adicionais
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Responsável Legal
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.responsavel_legal || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Inscrição Estadual
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.inscricao_estadual || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Inscrição Municipal
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.inscricao_municipal || 'Não informado'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Administrador da Empresa */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Administrador da Empresa
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Administrador
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.admin_nome || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email do Administrador
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {viewingCompany.admin_email || 'Não informado'}
                    </div>
                  </div>
                </div>
              </div>

              {/* E-mail Financeiro */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Mail className="h-5 w-5 mr-2" />
                  E-mail Financeiro
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail Financeiro - Contas a Pagar
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                    {viewingCompany.email_financeiro || 'Não informado'}
                  </div>
                </div>
              </div>

              {/* Status e Data de Criação */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Status e Data de Criação
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        viewingCompany.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {viewingCompany.ativo ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Ativa
                          </>
                        ) : (
                          <>
                            <X className="h-3 w-3 mr-1" />
                            Inativa
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Criação
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {formatDate(viewingCompany.criado_em) || 'Não informado'}
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Menu liberado
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {MENU_MODULOS.filter((item) => modulosIniciais(viewingCompany)[item.id]).map((item) => (
                      <span key={item.id} className="inline-flex px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-800 border border-emerald-200">
                        {item.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer do modal */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Fechar
              </button>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(viewingCompany);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
              >
                <Edit className="h-4 w-4 mr-2" />
                Editar Empresa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesPage;

